import {
  type Country,
  DATA_REGIONS,
  INVOICE_STATUS_TITLES,
  type Invoice,
  LICENSE_KIND_TITLES,
  LICENSE_KINDS,
  LIMIT_KINDS,
  LIMIT_TITLES,
  type LicenseKeyInfo,
  type LicenseKind,
  type OwnerInviteResult,
  type Partner,
  type Plan,
  type TenantDetail,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { Check, Copy, Download } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { ApiError, api } from '../../lib/api';
import { date, dateTime } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, ErrorBox, Spinner, toast, unboundErrors } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Panel } from '../../ui/layout';
import { InviteResult } from '../admin/InviteResult';
import { daysLeftText, LicenseEvents, LicenseHistory, moduleTitle, UsageBars } from '../admin/LicensePage';
import { CompanyConsoleBlocks, CompanyConsoleCard, EnterCompanyButton } from './CompanyConsole';
import { usePlatformRole } from './console-shared';
import { IssueInvoiceDialog } from './InvoicesPage';
import { limitsText, parseLimit, plansQuery, plusDays, StateBadge, todayIso } from './shared';

/** Поля формы лицензии со своей подписью ошибки */
const LICENSE_FIELDS = [
  'planId',
  'kind',
  'startsAt',
  'endsAt',
  'seats',
  'note',
  ...LIMIT_KINDS.map((k) => `limitsOverride.${k}`),
];

const tenantQuery = (id: string) => ({
  queryKey: ['platform', 'tenants', id],
  queryFn: () => api<TenantDetail>(`/platform/tenants/${id}`),
});

/** Счета компании (ЛЦ-06): последние счета и выставление нового */
function InvoicesBlock({ tenantId, plans }: { tenantId: string; plans: Plan[] | undefined }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ['platform', 'invoices', 'tenant', tenantId],
    queryFn: () => api<Invoice[]>('/platform/invoices', { query: { status: 'all', tenantId } }),
  });
  return (
    <Panel
      title="Счета"
      meta="ЛЦ-06"
      actions={
        <Button size="sm" variant="primary" onClick={() => setOpen(true)} disabled={!plans}>
          Выставить счёт
        </Button>
      }
    >
      {q.isPending ? <Spinner /> : q.isError ? <ErrorBox error={q.error} /> : null}
      {q.data?.length ? (
        <ul className="flex flex-col gap-2 text-[14px]">
          {q.data.slice(0, 5).map((inv) => (
            <li key={inv.id} className="flex flex-wrap items-baseline justify-between gap-x-3">
              <span className="text-ink">
                {inv.number} · {date(inv.issuedOn)}
              </span>
              <span className="num text-ink-2">
                {inv.amountTotal.toLocaleString('ru-RU')} {inv.currency},{' '}
                {inv.status === 'issued' && inv.overdue
                  ? INVOICE_STATUS_TITLES.overdue.toLowerCase()
                  : INVOICE_STATUS_TITLES[inv.status].toLowerCase()}
              </span>
            </li>
          ))}
        </ul>
      ) : q.data ? (
        <p className="text-[14px] text-ink-2">Счетов пока нет.</p>
      ) : null}
      <Link
        to="/platform/invoices"
        className="mt-3 inline-flex min-h-11 items-center text-[13px] text-ink-2 underline-offset-2 hover:underline md:min-h-0"
      >
        Все счета и оплаты
      </Link>
      {open && plans ? (
        <IssueInvoiceDialog tenantId={tenantId} plans={plans} onClose={() => setOpen(false)} />
      ) : null}
    </Panel>
  );
}

/** Ключ установки (ЛЦ-07): выпуск для установки у заказчика; сам ключ виден один раз */
function LicenseKeyBlock({ tenantId, tenantSlug }: { tenantId: string; tenantSlug: string }) {
  const qc = useQueryClient();
  const [installId, setInstallId] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const status = useQuery({
    queryKey: ['platform', 'license-keys', 'status'],
    queryFn: () => api<{ signingConfigured: boolean }>('/platform/license-keys/status'),
  });
  const keys = useQuery({
    queryKey: ['platform', 'license-keys', tenantId],
    queryFn: () => api<LicenseKeyInfo[]>(`/platform/tenants/${tenantId}/license-keys`),
  });
  const issue = useMutation({
    mutationFn: () =>
      api<{ token: string; info: LicenseKeyInfo }>(`/platform/tenants/${tenantId}/license-keys`, {
        body: { installId: installId.trim() || null },
      }),
    onSuccess: (r) => {
      setToken(r.token);
      qc.invalidateQueries({ queryKey: ['platform', 'license-keys', tenantId] });
    },
  });

  async function copy() {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token);
      toast('Ключ скопирован');
    } catch {
      toast('Не удалось скопировать — сохраните ключ в файл', 'danger');
    }
  }
  function save() {
    if (!token) return;
    const url = URL.createObjectURL(new Blob([token], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `epc-license-${tenantSlug}.key`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Panel title="Ключ установки" meta="ЛЦ-07">
      <p className="mb-3 text-[13px] text-ink-2">
        Для установки на серверах заказчика. Код установки заказчик видит в разделе «Лицензия»; без него ключ
        подойдёт любой установке.
      </p>
      {status.data && !status.data.signingConfigured ? (
        <p className="mb-3 rounded-md bg-surface-2 px-3 py-2 text-[13px] text-ink-2">
          Ключ подписи на портале не настроен — выпустить ключ нельзя.
        </p>
      ) : null}
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          issue.mutate();
        }}
      >
        <Field label="Код установки" htmlFor="lk-install" hint="Необязательно">
          <Input id="lk-install" value={installId} onChange={(e) => setInstallId(e.target.value)} />
        </Field>
        <div>
          <Button
            type="submit"
            size="sm"
            variant="primary"
            loading={issue.isPending}
            disabled={status.data ? !status.data.signingConfigured : false}
          >
            Выпустить ключ
          </Button>
        </div>
        <ErrorBox error={issue.error} />
      </form>
      {token ? (
        <div className="mt-3 rounded-md border border-warning/40 bg-warning-soft p-3">
          <p className="text-[13px] font-medium text-ink">
            Ключ показан один раз — скопируйте или сохраните в файл сейчас, потом его не открыть.
          </p>
          <textarea
            readOnly
            rows={4}
            value={token}
            aria-label="Ключ установки"
            onFocus={(e) => e.currentTarget.select()}
            className="mt-2 w-full rounded-md border border-line bg-surface p-2 font-mono text-[12px] break-all text-ink"
          />
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" icon={<Copy className="size-4" />} onClick={copy}>
              Копировать
            </Button>
            <Button size="sm" variant="secondary" icon={<Download className="size-4" />} onClick={save}>
              В файл
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setToken(null)}>
              Скрыть
            </Button>
          </div>
        </div>
      ) : null}
      <h3 className="mt-4 mb-1 text-[13px] font-medium text-ink">Выпущенные ключи</h3>
      {keys.isPending ? <Spinner /> : keys.isError ? <ErrorBox error={keys.error} /> : null}
      {keys.data?.length ? (
        <ul className="flex flex-col gap-2 text-[13px]">
          {keys.data.map((k) => (
            <li key={k.id} className="text-ink-2">
              <span className="text-ink">{k.planName}</span> · выпущен {date(k.issuedAt)}, действует до{' '}
              {k.endsAt ? date(k.endsAt) : 'без срока'}
              {k.installId ? ` · установка ${k.installId}` : ' · любая установка'}
              {k.revokedAt ? <Badge tone="danger">отозван {date(k.revokedAt)}</Badge> : null}
            </li>
          ))}
        </ul>
      ) : keys.data ? (
        <p className="text-[13px] text-ink-2">Ключей пока не выпускали.</p>
      ) : null}
    </Panel>
  );
}

/** Страна, регион данных и партнёр компании (МР-01, МР-07, МР-16) */
function MarketBlock({ tenantId }: { tenantId: string }) {
  const qc = useQueryClient();
  const countries = useQuery({
    queryKey: ['platform', 'countries'],
    queryFn: () => api<Country[]>('/platform/countries'),
  });
  const partners = useQuery({
    queryKey: ['platform', 'partners'],
    queryFn: () => api<Partner[]>('/platform/partners'),
  });
  const [country, setCountry] = useState('');
  const [region, setRegion] = useState('');
  const [partner, setPartner] = useState('');
  const done = () => qc.invalidateQueries({ queryKey: ['platform'] });
  const saveCountry = useMutation({
    mutationFn: () => api(`/platform/tenants/${tenantId}/country`, { body: { countryCode: country } }),
    onSuccess: () => {
      done();
      toast('Страна компании сохранена');
    },
  });
  const saveRegion = useMutation({
    mutationFn: () => api(`/platform/tenants/${tenantId}/region`, { body: { region } }),
    onSuccess: () => {
      done();
      toast('Регион данных изменён');
    },
  });
  const savePartner = useMutation({
    mutationFn: () =>
      api(`/platform/tenants/${tenantId}/partner`, { body: { partnerId: partner === '-' ? null : partner } }),
    onSuccess: () => {
      done();
      toast('Партнёр сохранён');
    },
  });
  return (
    <Panel title="Страна, регион данных, партнёр" meta="МР-01, МР-16">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Field
            label="Страна компании"
            htmlFor="mk-country"
            hint="Влияет на налог в счетах и функции страны"
          >
            <Select id="mk-country" value={country} onChange={(e) => setCountry(e.target.value)}>
              <option value="">— выберите —</option>
              {(countries.data ?? []).map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <Button
              size="sm"
              variant="secondary"
              disabled={!country}
              loading={saveCountry.isPending}
              onClick={() => saveCountry.mutate()}
            >
              Сохранить страну
            </Button>
          </div>
          <ErrorBox error={saveCountry.error} />
        </div>
        <div className="flex flex-col gap-2">
          <Field label="Регион данных" htmlFor="mk-region" hint="Где хранятся данные компании">
            <Select id="mk-region" value={region} onChange={(e) => setRegion(e.target.value)}>
              <option value="">— выберите —</option>
              {DATA_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <Button
              size="sm"
              variant="secondary"
              disabled={!region}
              loading={saveRegion.isPending}
              onClick={() => saveRegion.mutate()}
            >
              Сменить регион
            </Button>
          </div>
          <ErrorBox error={saveRegion.error} />
        </div>
        <div className="flex flex-col gap-2">
          <Field label="Партнёр" htmlFor="mk-partner" hint="Кто привёл компанию">
            <Select id="mk-partner" value={partner} onChange={(e) => setPartner(e.target.value)}>
              <option value="">— выберите —</option>
              <option value="-">Без партнёра</option>
              {(partners.data ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          </Field>
          <div>
            <Button
              size="sm"
              variant="secondary"
              disabled={!partner}
              loading={savePartner.isPending}
              onClick={() => savePartner.mutate()}
            >
              Сохранить партнёра
            </Button>
          </div>
          <ErrorBox error={savePartner.error} />
        </div>
      </div>
    </Panel>
  );
}

/** Выпуск или продление лицензии (ЛЦ-01, ЛЦ-02): тариф, вид, срок, места, свои лимиты */
function IssueLicenseDialog({
  data,
  plans,
  onClose,
}: {
  data: TenantDetail;
  plans: Plan[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const current = data.tenant.license;
  const today = todayIso();
  // Продление начинается там, где заканчивается действующая лицензия, если она ещё не истекла
  const start = current?.endsAt && current.endsAt > today ? current.endsAt : today;
  const [f, setF] = useState({
    planId: current?.planId ?? plans.find((p) => p.isActive)?.id ?? '',
    kind: (current?.kind === 'trial' ? 'saas' : (current?.kind ?? 'saas')) as LicenseKind,
    startsAt: start,
    endsAt: plusDays(start, 365) as string | null,
    perpetual: false,
    seats: current?.seats ? String(current.seats) : '',
    autoRenew: current?.autoRenew ?? false,
    override: Object.fromEntries(
      LIMIT_KINDS.map((k) => [
        k,
        current?.limitsOverride?.[k] != null ? String(current.limitsOverride[k]) : '',
      ]),
    ) as Record<(typeof LIMIT_KINDS)[number], string>,
    note: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const plan = plans.find((p) => p.id === f.planId);

  const save = useMutation({
    mutationFn: () => {
      const override: Record<string, number | null> = {};
      for (const k of LIMIT_KINDS) if (f.override[k].trim()) override[k] = parseLimit(f.override[k]);
      return api(`/platform/tenants/${data.tenant.id}/license`, {
        body: {
          planId: f.planId,
          kind: f.kind,
          startsAt: f.startsAt,
          endsAt: f.perpetual ? null : f.endsAt,
          seats: f.seats.trim() ? Number(f.seats) : null,
          autoRenew: f.autoRenew,
          limitsOverride: Object.keys(override).length ? override : null,
          note: f.note.trim() || null,
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
      toast(current ? 'Лицензия продлена' : 'Лицензия выпущена');
      onClose();
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setErrors({});
    save.mutate();
  }

  const setEnd = (days: number) =>
    setF((x) => ({ ...x, perpetual: false, endsAt: plusDays(x.startsAt, days) }));

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={current ? 'Продлить или изменить лицензию' : 'Выпустить лицензию'}
      description={`${data.tenant.name}. Прежняя лицензия уходит в историю, новая действует сразу.`}
      width="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="lic-form"
            loading={save.isPending}
            disabled={!f.planId}
          >
            {current ? 'Продлить' : 'Выпустить'}
          </Button>
        </>
      }
    >
      <form id="lic-form" onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <Field
          label="Тариф"
          htmlFor="lic-plan"
          error={errors.planId}
          hint={plan ? limitsText(plan.limits) : undefined}
        >
          <Select
            id="lic-plan"
            value={f.planId}
            onChange={(e) => setF((x) => ({ ...x, planId: e.target.value }))}
          >
            {plans
              .filter((p) => p.isActive || p.id === f.planId)
              .map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.isActive ? '' : ' (снят с продажи)'}
                </option>
              ))}
          </Select>
        </Field>
        <Field label="Вид лицензии" htmlFor="lic-kind" error={errors.kind}>
          <Select
            id="lic-kind"
            value={f.kind}
            onChange={(e) => setF((x) => ({ ...x, kind: e.target.value as LicenseKind }))}
          >
            {LICENSE_KINDS.map((k) => (
              <option key={k} value={k}>
                {LICENSE_KIND_TITLES[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Начало" htmlFor="lic-start" error={errors.startsAt}>
          <Input
            id="lic-start"
            type="date"
            value={f.startsAt}
            onChange={(e) => setF((x) => ({ ...x, startsAt: e.target.value }))}
            invalid={!!errors.startsAt}
          />
        </Field>
        <Field label="Окончание" htmlFor="lic-end" error={errors.endsAt}>
          <Input
            id="lic-end"
            type="date"
            value={f.perpetual ? '' : (f.endsAt ?? '')}
            disabled={f.perpetual}
            onChange={(e) => setF((x) => ({ ...x, endsAt: e.target.value || null }))}
            invalid={!!errors.endsAt}
          />
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => setEnd(30)}>
              +1 месяц
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEnd(365)}>
              +1 год
            </Button>
            <Checkbox
              label="Бессрочно"
              checked={f.perpetual}
              onChange={(e) => setF((x) => ({ ...x, perpetual: e.target.checked }))}
              className="ml-1 items-center"
            />
          </div>
        </Field>
        <Field
          label="Мест (пользователей)"
          htmlFor="lic-seats"
          hint="Пусто — по лимиту тарифа"
          error={errors.seats}
        >
          <Input
            id="lic-seats"
            type="number"
            inputMode="numeric"
            min={1}
            value={f.seats}
            onChange={(e) => setF((x) => ({ ...x, seats: e.target.value }))}
            invalid={!!errors.seats}
          />
        </Field>
        <Checkbox
          label="Автопродление"
          description="Напоминание о продлении приходит владельцу портала, а не компании"
          checked={f.autoRenew}
          onChange={(e) => setF((x) => ({ ...x, autoRenew: e.target.checked }))}
          className="sm:mt-6"
        />
        <fieldset className="sm:col-span-2">
          <legend className="mb-2 text-[13px] font-medium text-ink">
            Свои лимиты компании поверх тарифа
          </legend>
          <div className="grid gap-3 sm:grid-cols-5">
            {LIMIT_KINDS.map((k) => (
              <Field
                key={k}
                label={LIMIT_TITLES[k]}
                htmlFor={`lic-lim-${k}`}
                error={errors[`limitsOverride.${k}`]}
              >
                <Input
                  id={`lic-lim-${k}`}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  placeholder={plan?.limits[k] != null ? `по тарифу: ${plan.limits[k]}` : 'без ограничения'}
                  value={f.override[k]}
                  onChange={(e) => setF((x) => ({ ...x, override: { ...x.override, [k]: e.target.value } }))}
                />
              </Field>
            ))}
          </div>
          <p className="mt-1.5 text-[12px] text-ink-2">
            Пусто — как в тарифе. Число — лимит только для этой компании.
          </p>
        </fieldset>
        <Field
          label="Заметка"
          htmlFor="lic-note"
          hint="Номер договора или счёта, договорённости — видно компании на странице «Лицензия»"
          className="sm:col-span-2"
          error={errors.note}
        >
          <Textarea
            id="lic-note"
            value={f.note}
            onChange={(e) => setF((x) => ({ ...x, note: e.target.value }))}
            rows={2}
          />
        </Field>
        <ErrorBox
          className="sm:col-span-2"
          error={Object.keys(errors).length ? unboundErrors(errors, LICENSE_FIELDS) : save.error}
        />
      </form>
    </Dialog>
  );
}

function SuspendDialog({ data, onClose }: { data: TenantDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/platform/tenants/${data.tenant.id}/license/suspend`, { body: { reason } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast('Доступ компании приостановлен');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={`Приостановить доступ «${data.tenant.name}»`}
      description="Сотрудники компании не смогут войти, пока доступ не возобновлён. Данные сохраняются."
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            loading={save.isPending}
            disabled={reason.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Приостановить
          </Button>
        </>
      }
    >
      <Field
        label="Причина"
        htmlFor="susp-reason"
        hint="Видна администратору компании и записывается в журнал"
      >
        <Textarea id="susp-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
      </Field>
      <ErrorBox className="mt-3" error={save.error} />
    </Dialog>
  );
}

function RenameDialog({ data, onClose }: { data: TenantDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(data.tenant.name);
  const [isDemo, setIsDemo] = useState(data.tenant.isDemo);
  const save = useMutation({
    mutationFn: () => api(`/platform/tenants/${data.tenant.id}`, { method: 'PATCH', body: { name, isDemo } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast('Сохранено');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Карточка компании"
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={name.trim().length < 2}
            onClick={() => save.mutate()}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Название" htmlFor="ten-name">
          <Input id="ten-name" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Checkbox
          label="Демо-компания"
          description="Внешние каналы выключены: письма, Telegram и выгрузки наружу не уходят"
          checked={isDemo}
          onChange={(e) => setIsDemo(e.target.checked)}
        />
        <ErrorBox error={save.error} />
      </div>
    </Dialog>
  );
}

/** Новое приглашение администратора компании: ссылка показывается один раз (ЛЦ-05) */
function AdminInviteDialog({ data, onClose }: { data: TenantDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const [email, setEmail] = useState(data.pendingAdminInvite?.email ?? '');
  const [fullName, setFullName] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<OwnerInviteResult | null>(null);
  const [copied, setCopied] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api<OwnerInviteResult>(`/platform/tenants/${data.tenant.id}/admin-invite`, {
        body: { email, fullName },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      setResult(r);
    },
    onError: (err) => err instanceof ApiError && setErrors(err.fieldErrors()),
  });

  async function copy() {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast('Не удалось скопировать — выделите ссылку и скопируйте вручную', 'danger');
    }
  }

  if (result) {
    return (
      <Dialog
        open
        onOpenChange={() => undefined}
        title="Приглашение готово"
        description={`Для ${result.email}, действует до ${date(result.expiresAt)}. Ссылка показывается один раз.`}
        footer={
          <Button variant="primary" onClick={onClose}>
            Готово
          </Button>
        }
      >
        <div className="flex flex-col gap-2 sm:flex-row">
          <Input
            readOnly
            value={result.inviteLink}
            onFocus={(e) => e.currentTarget.select()}
            className="font-mono text-[13px]"
          />
          <Button
            variant="secondary"
            icon={copied ? <Check className="size-4" /> : <Copy className="size-4" />}
            onClick={copy}
          >
            {copied ? 'Скопировано' : 'Скопировать'}
          </Button>
        </div>
        <div className="mt-4">
          <InviteResult email={result.email} mail={result.mail} message={result.message} />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Приглашение администратора"
      description="Прежние открытые приглашения администратора отзываются. Принявший становится владельцем компании."
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" type="submit" form="inv-form" loading={save.isPending}>
            Создать ссылку
          </Button>
        </>
      }
    >
      <form
        id="inv-form"
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          setErrors({});
          save.mutate();
        }}
      >
        <Field label="Почта администратора" htmlFor="inv-email" error={errors.email}>
          <Input
            id="inv-email"
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            invalid={!!errors.email}
          />
        </Field>
        <Field label="Имя и фамилия" htmlFor="inv-name" error={errors.fullName}>
          <Input
            id="inv-name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            invalid={!!errors.fullName}
          />
        </Field>
        <ErrorBox error={Object.keys(errors).length ? null : save.error} />
      </form>
    </Dialog>
  );
}

/** Карточка компании у владельца портала: лицензия, лимиты, администраторы, история (ЛЦ-01…ЛЦ-05) */
/**
 * Карточка компании в кабинете: владельцу портала — с лицензией, счетами, ключом и рынком; администратору
 * портала — карточка команды портала без денег и действий с лицензией (ЛЦ-12).
 */
export function TenantPage() {
  return usePlatformRole() === 'owner' ? <OwnerTenantPage /> : <CompanyConsoleCard />;
}

function OwnerTenantPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const me = useMe();
  const qc = useQueryClient();
  const q = useQuery(tenantQuery(id));
  const plans = useQuery(plansQuery);
  const [dialog, setDialog] = useState<'license' | 'suspend' | 'rename' | 'invite' | 'reset' | null>(null);
  useCrumbs(q.data ? [{ label: q.data.tenant.name }] : null, q.error);

  const resume = useMutation({
    mutationFn: () => api(`/platform/tenants/${id}/license/resume`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      toast('Доступ компании возобновлён');
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось возобновить доступ', 'danger'),
  });

  // Демо-компания (ГЛ-02): вернуть исходные данные вручную, не дожидаясь ночи
  const resetDemo = useMutation({
    mutationFn: () =>
      api<{ deleted: number }>('/platform/demo/reset', {
        method: 'POST',
        body: { tenantId: id },
      }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['platform'] });
      setDialog(null);
      toast(`Демо-данные возвращены к исходным, удалено записей: ${r.deleted}`);
    },
    onError: (err) => toast(err instanceof Error ? err.message : 'Не удалось сбросить демо', 'danger'),
  });

  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const d = q.data;
  const { tenant, status } = d;
  const current = tenant.license;
  const own = me.tenant?.id === tenant.id;
  const modules = status.modules;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Link
            to="/platform"
            className="inline-flex h-11 items-center text-[14px] text-ink-2 underline-offset-4 hover:underline md:h-8"
          >
            ← Все компании
          </Link>
          <h2 className="text-[20px] leading-7 font-semibold text-ink [overflow-wrap:anywhere]">
            {tenant.name}
          </h2>
          {tenant.isDemo ? <Badge tone="info">демо</Badge> : null}
          <StateBadge state={status.state} />
        </div>
        <div className="flex flex-wrap gap-2">
          <EnterCompanyButton tenant={{ id: tenant.id, name: tenant.name }} />
          <Button variant="secondary" size="sm" onClick={() => setDialog('rename')}>
            Карточка
          </Button>
          {tenant.isDemo ? (
            <Button variant="secondary" size="sm" onClick={() => setDialog('reset')}>
              Сбросить демо-данные
            </Button>
          ) : null}
          {current?.suspended ? (
            <Button variant="primary" size="sm" loading={resume.isPending} onClick={() => resume.mutate()}>
              Возобновить доступ
            </Button>
          ) : (
            <>
              <Button variant="primary" size="sm" onClick={() => setDialog('license')} disabled={!plans.data}>
                {current ? 'Продлить лицензию' : 'Выпустить лицензию'}
              </Button>
              {current ? (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setDialog('suspend')}
                  disabled={own}
                  title={own ? 'Свою компанию приостановить нельзя' : undefined}
                >
                  Приостановить
                </Button>
              ) : null}
            </>
          )}
        </div>
      </div>

      {current?.suspended ? (
        <div className="rounded-md border border-danger/40 bg-danger-soft px-4 py-3 text-[14px] text-ink">
          Доступ приостановлен{current.suspendedReason ? `: ${current.suspendedReason}` : ''}. Сотрудники
          компании не могут войти в портал.
        </div>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="flex flex-col gap-5">
          <Panel title="Лицензия" meta={current ? `выпущена ${date(current.createdAt)}` : undefined}>
            <KeyValue
              items={[
                { label: 'Тариф', value: status.planName ?? '—' },
                { label: 'Вид', value: status.kind ? LICENSE_KIND_TITLES[status.kind] : '—' },
                { label: 'Начало', value: status.startsAt ? date(status.startsAt) : '—' },
                { label: 'Окончание', value: daysLeftText(status.daysLeft, status.endsAt) },
                { label: 'Мест', value: status.seats ?? 'по тарифу' },
                { label: 'Автопродление', value: current ? (current.autoRenew ? 'да' : 'нет') : '—' },
                ...(current?.limitsOverride
                  ? [
                      {
                        label: 'Свои лимиты',
                        value: LIMIT_KINDS.filter((k) => current.limitsOverride?.[k] != null)
                          .map((k) => `${LIMIT_TITLES[k]}: ${current.limitsOverride?.[k]}`)
                          .join(', '),
                      },
                    ]
                  : []),
                ...(current?.note ? [{ label: 'Заметка', value: current.note }] : []),
                {
                  label: 'Разделы',
                  value:
                    modules === null
                      ? 'все разделы портала'
                      : modules.length
                        ? modules.map(moduleTitle).join(', ')
                        : 'только основа',
                },
              ]}
            />
          </Panel>
          <Panel title="Использование лимитов" meta="ЛЦ-04">
            <UsageBars usage={status.usage} />
          </Panel>
          <CompanyConsoleBlocks id={tenant.id} />
          <Panel title="Журнал по компании" meta="ЛЦ-05">
            <LicenseEvents events={d.events} />
          </Panel>
        </div>
        <div className="flex flex-col gap-5">
          <Panel title="О компании">
            <KeyValue
              items={[
                { label: 'Код', value: tenant.slug },
                { label: 'Создана', value: dateTime(tenant.createdAt) },
                { label: 'Сотрудников', value: tenant.members },
                { label: 'Проектов', value: tenant.projects },
                {
                  label: 'Состояние',
                  value:
                    tenant.status === 'active'
                      ? 'работает'
                      : tenant.status === 'suspended'
                        ? 'приостановлена'
                        : tenant.status,
                },
              ]}
            />
          </Panel>
          <Panel title="Администраторы компании" meta="кому писать">
            {d.admins.length ? (
              <ul className="flex flex-col gap-2 text-[14px]">
                {d.admins.map((a) => (
                  <li key={a.email} className="flex flex-wrap items-baseline justify-between gap-x-3">
                    <span className="text-ink">
                      {a.fullName}
                      {a.isOwner ? <span className="ml-1.5 text-[12px] text-ink-2">владелец</span> : null}
                    </span>
                    <a
                      className="inline-flex min-h-11 items-center text-ink-2 underline-offset-2 hover:underline md:min-h-0"
                      href={`mailto:${a.email}`}
                    >
                      {a.email}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">Администраторов пока нет.</p>
            )}
            {d.pendingAdminInvite ? (
              <p className="mt-3 rounded-md bg-surface-2 px-3 py-2 text-[13px] text-ink-2">
                Приглашение для {d.pendingAdminInvite.email} ещё не принято, действует до{' '}
                {date(d.pendingAdminInvite.expiresAt)}.
              </p>
            ) : null}
            {/* Ссылка приглашения потерялась, истекла или почта с опечаткой — новое приглашение, прежнее отзывается */}
            <Button className="mt-3" size="sm" variant="secondary" onClick={() => setDialog('invite')}>
              {d.admins.length ? 'Пригласить ещё администратора' : 'Новое приглашение администратора'}
            </Button>
          </Panel>
          <InvoicesBlock tenantId={tenant.id} plans={plans.data} />
          <LicenseKeyBlock tenantId={tenant.id} tenantSlug={tenant.slug} />
          <MarketBlock tenantId={tenant.id} />
          <Panel title="Прежние лицензии" meta="ЛЦ-02">
            <LicenseHistory history={d.history} />
          </Panel>
        </div>
      </div>

      {dialog === 'license' && plans.data ? (
        <IssueLicenseDialog data={d} plans={plans.data} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'suspend' ? <SuspendDialog data={d} onClose={() => setDialog(null)} /> : null}
      {dialog === 'rename' ? <RenameDialog data={d} onClose={() => setDialog(null)} /> : null}
      {dialog === 'invite' ? <AdminInviteDialog data={d} onClose={() => setDialog(null)} /> : null}
      <Dialog
        open={dialog === 'reset'}
        onOpenChange={(v) => !v && setDialog(null)}
        width="sm"
        title="Вернуть демо к исходным данным?"
        description="Все записи демо-компании будут удалены и заведены заново, открытые демо-сессии закончатся. Ночью это происходит само."
        footer={
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>
              Отмена
            </Button>
            <Button variant="danger" loading={resetDemo.isPending} onClick={() => resetDemo.mutate()}>
              Сбросить
            </Button>
          </>
        }
      >
        <p className="text-[14px] text-ink-2">
          Сброс занимает несколько секунд. Люди, которые сейчас смотрят демо, увидят страницу входа.
        </p>
      </Dialog>
    </div>
  );
}
